// src/PendenciasConcluidasView.js
// Consulta de pendências concluídas.
// Busca todas as concluídas uma vez (uma única condição: status == 'Concluído', sem índice composto)
// e aplica os filtros na tela, sem diferenciar maiúsculas nem acentos.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useConcluidas } from './concluidasStore';
import { api } from './api';
import { recarregar } from './dados';
import { BuscaSelect } from './ui';

// ---------- utilitários ----------
const normalizar = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const soDigitos = (s) => String(s ?? '').replace(/\D/g, '');

const textoParaData = (dt, hora) => {
    const m = String(dt || '').match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (!m) return null;
    const [h, mi] = String(hora || '00:00').split(':').map(Number);
    return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), h || 0, mi || 0);
};
const tsParaData = (ts) => (ts && typeof ts.toDate === 'function' ? ts.toDate() : ts instanceof Date ? ts : null);

// Data de conclusão: o timestamp; senão o texto dtconclusao; senão a última alteração (pendências antigas)
const dataConclusao = (t) =>
    tsParaData(t.timestampconclusao) || textoParaData(t.dtconclusao) || tsParaData(t.timestampAlteracao) || textoParaData(t.dtAlteracao, t.horaAlteracao);
const dataAbertura = (t) => tsParaData(t.timestampCriacao) || textoParaData(t.dtLancamento, t.hora);

const fmtData = (d) => (d ? d.toLocaleDateString('pt-BR') : '—');
const fmtDataHora = (d) => (d ? `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : '—');
const isoDia = (d) => {
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const diasEntre = (a, b) => (a && b ? Math.max(0, Math.round((b - a) / 86400000)) : null);

const POR_PAGINA = 50;

// Atalhos de período
const PERIODOS = [
    { id: 'hoje', label: 'Hoje', range: () => { const h = new Date(); return [h, h]; } },
    { id: '7', label: '7 dias', range: () => { const h = new Date(); const i = new Date(); i.setDate(h.getDate() - 6); return [i, h]; } },
    { id: '30', label: '30 dias', range: () => { const h = new Date(); const i = new Date(); i.setDate(h.getDate() - 29); return [i, h]; } },
    { id: 'mes', label: 'Este mês', range: () => { const h = new Date(); return [new Date(h.getFullYear(), h.getMonth(), 1), new Date(h.getFullYear(), h.getMonth() + 1, 0)]; } },
    { id: 'mesant', label: 'Mês passado', range: () => { const h = new Date(); return [new Date(h.getFullYear(), h.getMonth() - 1, 1), new Date(h.getFullYear(), h.getMonth(), 0)]; } },
    { id: 'tudo', label: 'Tudo', range: () => [null, null] },
];

// ---------- ícones ----------
const Svg = ({ className = 'h-4 w-4', children }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IconBusca = (p) => <Svg {...p}><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></Svg>;
const IconCheck = (p) => <Svg {...p}><path d="M5 13l4 4L19 7" /></Svg>;
const IconDownload = (p) => <Svg {...p}><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></Svg>;
const IconChevron = ({ aberto, ...p }) => <Svg {...p}><path d={aberto ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} /></Svg>;

// ---------- campo de empresa com busca enquanto digita ----------
const FiltroEmpresa = ({ empresas, valor, onChange }) => {
    const [texto, setTexto] = useState('');
    const [aberto, setAberto] = useState(false);
    const [destaque, setDestaque] = useState(0);
    const listaRef = useRef(null);

    // mostra o nome quando a empresa é escolhida (ou limpa quando o filtro é limpo)
    useEffect(() => {
        const e = empresas.find((x) => x.idFirebase === valor);
        setTexto(e ? e.nome : '');
    }, [valor]); // eslint-disable-line react-hooks/exhaustive-deps

    const opcoes = useMemo(() => {
        const t = normalizar(texto);
        if (!t) return empresas;
        const comeca = [];
        const contem = [];
        empresas.forEach((e) => {
            const n = normalizar(e.nome);
            if (n.startsWith(t)) comeca.push(e);
            else if (n.includes(t)) contem.push(e);
        });
        return [...comeca, ...contem];
    }, [empresas, texto]);

    useEffect(() => { setDestaque(0); }, [texto]);
    useEffect(() => {
        const el = listaRef.current && listaRef.current.children[destaque];
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }, [destaque]);

    const escolher = (e) => { onChange(e.idFirebase); setTexto(e.nome); setAberto(false); };

    return (
        <div className="relative">
            <input
                type="text"
                value={texto}
                autoComplete="off"
                placeholder="Todas as empresas"
                onChange={(e) => { setTexto(e.target.value.toUpperCase()); setAberto(true); if (valor) onChange(''); }}
                onFocus={(e) => { setAberto(true); e.target.select(); }}
                onBlur={() => setTimeout(() => setAberto(false), 150)}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') { e.preventDefault(); setAberto(true); setDestaque((i) => Math.min(i + 1, Math.max(0, opcoes.length - 1))); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setDestaque((i) => Math.max(i - 1, 0)); }
                    else if (e.key === 'Enter') { e.preventDefault(); if (opcoes[destaque]) escolher(opcoes[destaque]); }
                    else if (e.key === 'Escape') setAberto(false);
                }}
                className={`w-full h-9 border rounded-md pl-3 pr-7 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-indigo-300 ${valor ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300 bg-white'}`}
            />
            {texto && (
                <button type="button" tabIndex={-1} title="Limpar"
                    onMouseDown={(e) => { e.preventDefault(); setTexto(''); onChange(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-lg leading-none">×</button>
            )}
            {aberto && (
                <ul ref={listaRef} className="absolute z-30 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto text-sm">
                    {opcoes.length ? opcoes.map((e, i) => (
                        <li key={e.idFirebase}
                            onMouseDown={(ev) => { ev.preventDefault(); escolher(e); }}
                            onMouseEnter={() => setDestaque(i)}
                            className={`px-3 py-1.5 cursor-pointer ${i === destaque ? 'bg-indigo-100' : ''}`}>
                            {e.nome}
                        </li>
                    )) : <li className="px-3 py-2 text-gray-400">Nenhuma empresa encontrada</li>}
                </ul>
            )}
        </div>
    );
};

const Rotulo = ({ children }) => <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{children}</span>;

// ======================================================================
const PendenciasConcluidasView = ({ empresas, usuarios, consultores, modulos, onViewPendency, setCurrentView }) => {
    // pendências concluídas compartilhadas (carregadas uma vez por sessão)
    const { concluidas: todas, carregando: loading, erro: erroCarga } = useConcluidas();
    const erro = erroCarga ? (erroCarga.code === 'resource-exhausted' ? 'A cota diária do Firebase foi atingida. Tente novamente mais tarde.' : 'Não foi possível carregar as pendências concluídas.') : '';

    // filtros
    const [busca, setBusca] = useState('');
    const [filterEmpresa, setFilterEmpresa] = useState('');
    const [filterTecnico, setFilterTecnico] = useState('');
    const [filterModulo, setFilterModulo] = useState('');
    const [filterSolicitacao, setFilterSolicitacao] = useState('');
    const [filterAcaoTomada, setFilterAcaoTomada] = useState('');
    const [periodo, setPeriodo] = useState('30');
    const [startDate, setStartDate] = useState(() => isoDia(PERIODOS[2].range()[0]));
    const [endDate, setEndDate] = useState(() => isoDia(new Date()));
    const [maisFiltros, setMaisFiltros] = useState(false);

    const [ordem, setOrdem] = useState('conclusao_desc');
    const [limite, setLimite] = useState(POR_PAGINA);
    const [expandida, setExpandida] = useState(null);

    const aplicarPeriodo = (id) => {
        setPeriodo(id);
        const [i, f] = PERIODOS.find((p) => p.id === id).range();
        setStartDate(i ? isoDia(i) : '');
        setEndDate(f ? isoDia(f) : '');
    };

    // nome do módulo escolhido (pendências antigas podem não ter moduloId)
    const moduloSel = modulos.find((m) => m.idFirebase === filterModulo);
    const empresaSel = empresas.find((e) => e.idFirebase === filterEmpresa);

    const filtradas = useMemo(() => {
        const termo = normalizar(busca);
        const termoNum = soDigitos(busca);
        const sol = normalizar(filterSolicitacao);
        const acao = normalizar(filterAcaoTomada);
        const tec = normalizar(filterTecnico);
        const ini = startDate ? new Date(`${startDate}T00:00:00`) : null;
        const fim = endDate ? new Date(`${endDate}T23:59:59.999`) : null;

        const lista = todas.map((t) => ({ ...t, _concl: dataConclusao(t), _abert: dataAbertura(t) })).filter((t) => {
            if (filterEmpresa && !(t.empresaId === filterEmpresa || (!t.empresaId && empresaSel && normalizar(t.empresa) === normalizar(empresaSel.nome)))) return false;
            if (tec && normalizar(t.tecnico) !== tec) return false;
            if (filterModulo && !(t.moduloId === filterModulo || (!t.moduloId && moduloSel && normalizar(t.modulo) === normalizar(moduloSel.descricao)))) return false;
            if (ini && (!t._concl || t._concl < ini)) return false;
            if (fim && (!t._concl || t._concl > fim)) return false;
            if (sol && !normalizar(t.descricao).includes(sol)) return false;
            if (acao && !normalizar(t.conclusao).includes(acao)) return false;
            if (termo) {
                const achouTexto = [t.empresa, t.usuario, t.chamado, t.modulo, t.tecnico, t.descricao, t.conclusao]
                    .some((v) => normalizar(v).includes(termo));
                const achouNumero = termoNum && (String(t.nrPendenciaSequencial || '') === termoNum || soDigitos(t.telefoneUsuario).includes(termoNum));
                if (!achouTexto && !achouNumero) return false;
            }
            return true;
        });

        const val = (d) => (d ? d.getTime() : 0);
        const ordens = {
            conclusao_desc: (a, b) => val(b._concl) - val(a._concl),
            conclusao_asc: (a, b) => val(a._concl) - val(b._concl),
            numero_desc: (a, b) => (b.nrPendenciaSequencial || 0) - (a.nrPendenciaSequencial || 0),
            empresa: (a, b) => String(a.empresa || '').localeCompare(String(b.empresa || '')),
            tempo_desc: (a, b) => (diasEntre(b._abert, b._concl) ?? -1) - (diasEntre(a._abert, a._concl) ?? -1),
        };
        return lista.sort(ordens[ordem]);
    }, [todas, busca, filterEmpresa, filterTecnico, filterModulo, filterSolicitacao, filterAcaoTomada, startDate, endDate, ordem, empresaSel, moduloSel]);

    // volta para a primeira página quando os filtros mudam
    useEffect(() => { setLimite(POR_PAGINA); setExpandida(null); },
        [busca, filterEmpresa, filterTecnico, filterModulo, filterSolicitacao, filterAcaoTomada, startDate, endDate, ordem]);

    // resumo
    const resumo = useMemo(() => {
        const tempos = filtradas.map((t) => diasEntre(t._abert, t._concl)).filter((x) => x != null);
        const media = tempos.length ? tempos.reduce((a, b) => a + b, 0) / tempos.length : null;
        const porTecnico = {};
        filtradas.forEach((t) => { const k = t.tecnico || '—'; porTecnico[k] = (porTecnico[k] || 0) + 1; });
        const top = Object.entries(porTecnico).sort((a, b) => b[1] - a[1]).slice(0, 3);
        return { total: filtradas.length, media, top };
    }, [filtradas]);

    const filtrosAtivos = [busca, filterEmpresa, filterTecnico, filterModulo, filterSolicitacao, filterAcaoTomada].filter(Boolean).length + (periodo !== 'tudo' ? 1 : 0);

    const handleClearFilters = () => {
        setBusca(''); setFilterEmpresa(''); setFilterTecnico(''); setFilterModulo('');
        setFilterSolicitacao(''); setFilterAcaoTomada('');
        aplicarPeriodo('tudo');
    };

    const handleReopenPendency = async (task) => {
        if (!window.confirm(`Reabrir a pendência ${task.nrPendenciaSequencial || task.chamado || ''} (${task.empresa})? Ela voltará para "Pendente".`)) return;
        try {
            // o servidor volta para "Pendente", limpa a data de conclusão e grava o histórico
            await api.post(`/pendencias/${task.id}/status`, { status: 'Pendente' });
            recarregar('pendencias');
            setCurrentView('pendencias');
        } catch (error) {
            console.error('Erro ao reabrir pendência:', error);
            alert(`Erro ao reabrir a pendência: ${error.message}`);
        }
    };

    // exporta o resultado filtrado para Excel (CSV com ; e acentos)
    const exportarCSV = () => {
        const col = ['Nr', 'Empresa', 'Módulo', 'Usuário', 'Telefone', 'Técnico', 'Chamado', 'Aberta em', 'Concluída em', 'Dias', 'Solicitação', 'Ação tomada'];
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`;
        const linhas = filtradas.map((t) => [
            t.nrPendenciaSequencial, t.empresa, t.modulo, t.usuario, t.telefoneUsuario, t.tecnico, t.chamado,
            fmtData(t._abert), fmtData(t._concl), diasEntre(t._abert, t._concl) ?? '', t.descricao, t.conclusao,
        ].map(esc).join(';'));
        const blob = new Blob(['﻿' + [col.map(esc).join(';'), ...linhas].join('\r\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pendencias_concluidas_${isoDia(new Date())}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const visiveis = filtradas.slice(0, limite);
    const inputCls = 'w-full h-9 border border-gray-300 rounded-md px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300';

    return (
        <div className="max-w-7xl mx-auto space-y-4">
            {/* ---------- cabeçalho ---------- */}
            <div className="flex flex-wrap items-end gap-3">
                <div className="mr-auto">
                    <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                        <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-green-100 text-green-700"><IconCheck className="h-5 w-5" /></span>
                        Pendências Concluídas
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {loading ? 'Carregando...' : `${todas.length} concluída${todas.length === 1 ? '' : 's'} no total`}
                    </p>
                </div>
                <button type="button" onClick={exportarCSV} disabled={!filtradas.length}
                    className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    <IconDownload /> Exportar Excel
                </button>
            </div>

            {/* ---------- filtros ---------- */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
                <div className="relative">
                    <IconBusca className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        placeholder="Buscar por empresa, nº da pendência, chamado, usuário, telefone, solicitação ou ação tomada..."
                        className="w-full h-10 border border-gray-300 rounded-md pl-9 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                    />
                    {busca && <button type="button" onClick={() => setBusca('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-lg leading-none">×</button>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <label className="block"><Rotulo>Empresa</Rotulo>
                        <FiltroEmpresa empresas={empresas} valor={filterEmpresa} onChange={setFilterEmpresa} />
                    </label>
                    <label className="block"><Rotulo>Técnico</Rotulo>
                        <BuscaSelect placeholder="Todos os técnicos" vazio="Nenhum técnico encontrado"
                            valor={filterTecnico} onChange={setFilterTecnico}
                            opcoes={consultores.map((c) => ({ valor: c.nome, rotulo: c.nome || '', detalhe: c.ativo === false ? 'inativo' : '' }))} />
                    </label>
                    <label className="block"><Rotulo>Módulo</Rotulo>
                        <BuscaSelect placeholder="Todos os módulos" vazio="Nenhum módulo encontrado"
                            valor={filterModulo} onChange={setFilterModulo}
                            opcoes={modulos.map((m) => ({ valor: m.idFirebase, rotulo: m.descricao || '', detalhe: m.ativo === false ? 'inativo' : '' }))} />
                    </label>
                    <label className="block"><Rotulo>Ordenar por</Rotulo>
                        <select value={ordem} onChange={(e) => setOrdem(e.target.value)} className={inputCls}>
                            <option value="conclusao_desc">Conclusão mais recente</option>
                            <option value="conclusao_asc">Conclusão mais antiga</option>
                            <option value="numero_desc">Nº da pendência</option>
                            <option value="empresa">Empresa (A–Z)</option>
                            <option value="tempo_desc">Mais demoradas</option>
                        </select>
                    </label>
                </div>

                {/* período */}
                <div className="flex flex-wrap items-end gap-2">
                    <div className="w-full sm:w-auto">
                        <Rotulo>Concluídas em</Rotulo>
                        <div className="inline-flex flex-wrap rounded-md border border-gray-300 overflow-hidden">
                            {PERIODOS.map((p) => (
                                <button key={p.id} type="button" onClick={() => aplicarPeriodo(p.id)}
                                    className={`px-3 h-9 text-sm border-r last:border-r-0 border-gray-300 ${periodo === p.id ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <label className="block"><Rotulo>De</Rotulo>
                        <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPeriodo('custom'); }} className={inputCls} />
                    </label>
                    <label className="block"><Rotulo>Até</Rotulo>
                        <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPeriodo('custom'); }} className={inputCls} />
                    </label>

                    <div className="ml-auto flex items-center gap-2">
                        <button type="button" onClick={() => setMaisFiltros((v) => !v)} className="h-9 px-3 text-sm text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1">
                            <IconChevron aberto={maisFiltros} /> Solicitação / Ação tomada
                        </button>
                        {filtrosAtivos > 0 && (
                            <button type="button" onClick={handleClearFilters} className="h-9 px-3 rounded-md bg-gray-100 hover:bg-gray-200 text-sm text-gray-700">
                                Limpar filtros ({filtrosAtivos})
                            </button>
                        )}
                    </div>
                </div>

                {maisFiltros && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                        <label className="block"><Rotulo>Solicitação do cliente contém</Rotulo>
                            <input type="text" value={filterSolicitacao} onChange={(e) => setFilterSolicitacao(e.target.value)} placeholder="ex.: nota fiscal" className={inputCls} />
                        </label>
                        <label className="block"><Rotulo>Ação tomada contém</Rotulo>
                            <input type="text" value={filterAcaoTomada} onChange={(e) => setFilterAcaoTomada(e.target.value)} placeholder="ex.: atualizado sistema" className={inputCls} />
                        </label>
                    </div>
                )}
            </div>

            {/* ---------- resumo ---------- */}
            {!loading && !erro && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Encontradas</div>
                        <div className="text-2xl font-bold text-gray-800">{resumo.total}</div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tempo médio até concluir</div>
                        <div className="text-2xl font-bold text-gray-800">
                            {resumo.media == null ? '—' : `${resumo.media.toFixed(1).replace('.', ',')} dia${resumo.media >= 1.95 ? 's' : ''}`}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Mais concluídas por</div>
                        {resumo.top.length ? resumo.top.map(([nome, n]) => (
                            <div key={nome} className="flex justify-between text-sm text-gray-700"><span className="truncate">{nome}</span><b>{n}</b></div>
                        )) : <div className="text-sm text-gray-400">—</div>}
                    </div>
                </div>
            )}

            {/* ---------- lista ---------- */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-gray-500">Carregando pendências concluídas...</div>
                ) : erro ? (
                    <div className="p-10 text-center text-red-600">{erro}</div>
                ) : filtradas.length === 0 ? (
                    <div className="p-10 text-center text-gray-500">
                        Nenhuma pendência concluída com esses filtros.
                        {filtrosAtivos > 0 && <button type="button" onClick={handleClearFilters} className="ml-2 text-indigo-600 hover:underline">Limpar filtros</button>}
                    </div>
                ) : (
                    <>
                        {/* cabeçalho das colunas (telas grandes) */}
                        <div className="hidden lg:grid grid-cols-[5rem_1.6fr_1.4fr_1fr_7rem_8rem_9.5rem] gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            <span>Nr</span><span>Empresa</span><span>Módulo · Usuário</span><span>Técnico</span><span>Chamado</span><span>Concluída</span><span className="text-right">Ações</span>
                        </div>
                        <ul className="divide-y divide-gray-100">
                            {visiveis.map((t) => {
                                const dias = diasEntre(t._abert, t._concl);
                                const aberta = expandida === t.id;
                                return (
                                    <li key={t.id} className={aberta ? 'bg-indigo-50/40' : 'hover:bg-gray-50'}>
                                        <div
                                            onClick={() => setExpandida(aberta ? null : t.id)}
                                            className="grid grid-cols-[4.5rem_1fr] lg:grid-cols-[5rem_1.6fr_1.4fr_1fr_7rem_8rem_9.5rem] gap-x-3 gap-y-1 px-4 py-2.5 items-center cursor-pointer text-sm"
                                        >
                                            <span className="font-bold text-indigo-700">#{t.nrPendenciaSequencial || '—'}</span>
                                            <span className="font-semibold text-gray-800 uppercase truncate" title={t.empresa}>{t.empresa}</span>
                                            <span className="col-start-2 lg:col-start-auto text-gray-600 truncate uppercase" title={`${t.modulo || ''} · ${t.usuario || ''}`}>
                                                {t.modulo || '—'} <span className="text-gray-400">·</span> {t.usuario || '—'}
                                            </span>
                                            <span className="col-start-2 lg:col-start-auto text-gray-600 truncate">{t.tecnico || '—'}</span>
                                            <span className="col-start-2 lg:col-start-auto text-gray-600">{t.chamado ? `Ch. ${t.chamado}` : <span className="text-gray-300">—</span>}</span>
                                            <span className="col-start-2 lg:col-start-auto text-gray-700">
                                                {fmtData(t._concl)}
                                                {dias != null && (
                                                    <span title="Dias entre abertura e conclusão"
                                                        className={`ml-1.5 text-[11px] font-bold rounded-full px-1.5 py-px ${dias >= 15 ? 'bg-red-100 text-red-700' : dias >= 7 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-700'}`}>
                                                        {dias}d
                                                    </span>
                                                )}
                                            </span>
                                            <span className="col-start-2 lg:col-start-auto flex lg:justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                <button type="button" onClick={() => onViewPendency('pendencia_cadastro', t)}
                                                    className="h-7 px-2.5 rounded border border-indigo-200 text-indigo-700 hover:bg-indigo-50 text-xs font-semibold">Abrir</button>
                                                <button type="button" onClick={() => handleReopenPendency(t)}
                                                    className="h-7 px-2.5 rounded border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold"
                                                    title="Volta a pendência para 'Pendente'">Reabrir</button>
                                            </span>
                                        </div>

                                        {/* detalhe: solicitação e ação tomada */}
                                        {aberta && (
                                            <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                                <div className="bg-white rounded-md border border-gray-200 p-3">
                                                    <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Solicitação do cliente</div>
                                                    <p className="text-gray-800 whitespace-pre-wrap">{t.descricao || '—'}</p>
                                                </div>
                                                <div className="bg-white rounded-md border border-green-200 p-3">
                                                    <div className="text-xs font-semibold text-green-700 uppercase mb-1">Ação tomada</div>
                                                    <p className="text-gray-800 whitespace-pre-wrap">{t.conclusao || '—'}</p>
                                                </div>
                                                <div className="md:col-span-2 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                                                    <span>Aberta em {fmtDataHora(t._abert)}</span>
                                                    <span>Concluída em {fmtDataHora(t._concl)}</span>
                                                    {t.telefoneUsuario && <span>Telefone: {t.telefoneUsuario}</span>}
                                                    {t.setor && <span>Setor: {t.setor}</span>}
                                                </div>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600">
                            <span>Mostrando {visiveis.length} de {filtradas.length}</span>
                            {visiveis.length < filtradas.length && (
                                <button type="button" onClick={() => setLimite((l) => l + POR_PAGINA)}
                                    className="h-8 px-4 rounded-md bg-white border border-gray-300 hover:bg-gray-100 text-gray-700">
                                    Carregar mais {Math.min(POR_PAGINA, filtradas.length - visiveis.length)}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PendenciasConcluidasView;
